import React, { Fragment, useState } from "react";
import { ReactSVG } from "react-svg";
import { Menu, Transition } from "@headlessui/react";

import Button from "../../../../../common/Button";
import NewToolTip from "../../../../../common/New-ToolTip";
import UncodedMedical from "../../../../../common/tooltip/patient/uncoded-medical";

import { SymptomControlOptions } from "../../../../create/options";
import { formatDateToDD_MMM_YYYY, formatWordsIntoLines, formatDiagnosisDateForDisplay } from "../../../../../../helpers/functions";
import { useClickOutsideTooltip } from "../../../../../../hooks/useClickOutsideTooltip";

const HistoryTable = ({ headerName, isMedicalConditionTable, records, confirmedRecords, onConfirm, onConfirmAll, onEdit, onDelete, onRestore, showConfirmation = true }) => {
    const [openTooltipId, setOpenTooltipId] = useState(null);
    const [openMenuIndex, setOpenMenuIndex] = useState(null);
    const tooltipWrapperRef = useClickOutsideTooltip(openTooltipId, setOpenTooltipId);

    const isAllConfirmed = () => {
        if(confirmedRecords && records) {
            // here it possible that we may have some dummy values stored in backend so that will match count but actually that condition is not confirmed
            return records.filter((item) => item.status === 'active' && !confirmedRecords.includes(item.id)).length === 0;
        } else {
            return false;
        }
    }

    const isCurrentEntryConfirmed = (id) => {
        return confirmedRecords && confirmedRecords.includes(id);
    }

    const isInValidGenCode = (item) => {
        return isMedicalConditionTable ? item.medicalConditionGenCode === null : item.studyGenCode === null;
    }

    const renderCommentsTooltip = (comment) => (
        <div className="bg-white max-w-[460px]">
            <div className="flex flex-col gap-3 p-2">
                <div className="text-teal-300 text-sm-body prose-a:underline">
                    <div className="flex flex-col gap-4">
                        <p style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                            {formatWordsIntoLines(comment)}
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="rounded-[6px] bg-white border border-gray-100 p-3">
            <h4 className="text-2xl font-bold text-teal-600 pb-2 pl-2">
                {headerName}
            </h4>
            <div className="rounded-[6px] border border-gray-100">
                {/* Scroll after 6 visible rows */}
                <div
                    className={`w-full ${ records && records.length > 6 ? "overflow-y-auto scrollbar-hide max-h-[290px]" : ""}`}
                >
                <table className="min-w-full divide-y bg-gray-50">
                    <thead className="items-start gap-4">
                        <tr className="h-[56px]">
                            <th
                                scope="col"
                                className="pl-6 w-[28%] text-base font-semibold text-left text-gray-900"
                            >
                                {isMedicalConditionTable ? 'Medical Condition' : 'Medical Study'}
                            </th>
                            <th
                                scope="col"
                                className="w-[10%] text-base font-semibold text-left text-gray-900"
                            >
                                Status
                            </th>
                            <th
                                scope="col"
                                className="w-[16%] text-base font-semibold text-left text-gray-900"
                            >
                                {isMedicalConditionTable ? 'Symptom Control' : 'Medication Name'}
                            </th>
                            <th
                                scope="col"
                                className="w-[12%] text-base font-semibold text-left text-gray-900"
                            >
                                Source
                            </th>
                            <th
                                scope="col"
                                className="w-[11%] text-base font-semibold text-left text-gray-900"
                            >
                                Diagnosis Date
                            </th>
                            <th
                                scope="col"
                                className="w-[9%] text-base font-semibold text-center text-gray-900"
                            >
                                Comments
                            </th>
                            <th
                                scope="col"
                                className="w-[13%] text-base font-semibold text-center text-gray-900"
                            >
                                {showConfirmation && !records.every(val => val.status === "inactive") ? (
                                    <Button
                                        className="text-base rounded-[4px] !w-[220px]"
                                        type={isAllConfirmed() ? "primary" : "primary-outline"}
                                        text={isAllConfirmed() ? "Confirmed" : "Confirm All"}
                                        onClick={() => {
                                            if (!isAllConfirmed()) {
                                                onConfirmAll();
                                            }
                                        }}
                                    />
                                ) : (
                                    <span>Actions</span>
                                )}
                            </th>
                        </tr>
                    </thead>
                    <tbody className="min-w-full divide-y bg-white gap-4">
                        {records?.map((item, i) => {
                            const isStripedRow = i % 2 === 1;
                            const rowBgClass =
                                item.status === "inactive" ? "bg-red-50" : isCurrentEntryConfirmed(item.id)
                                        ? "bg-blue-100" : isStripedRow ? "bg-gray-10" : "bg-white";
                            return (
                                <tr
                                    className={`${rowBgClass} transition-colors duration-300 m-4 cursor-pointer group `}
                                    key={item.id}
                                >
                                <td
                                    className={`pl-6 py-2 w-[28%] text-base font-medium text-teal-300 whitespace-nowrap ${isInValidGenCode(item) ? "text-pink" : "text-gray-800"}`}
                                    data-tooltip-id={isInValidGenCode(item) ? `ma-uncoded-${isMedicalConditionTable ? 'medical' : 'medStudy'}` : ""}
                                >
                                    <span className="capitalize">
                                        {item.name}
                                    </span>
                                    {isInValidGenCode(item) && (
                                        <NewToolTip
                                            id={`ma-uncoded-${isMedicalConditionTable ? 'medical' : 'medStudy'}`}
                                            render={() => <UncodedMedical />}
                                        />
                                    )}
                                </td>
                                <td className="w-[10%] text-base text-gray-800 whitespace-nowrap capitalize">
                                    {item.status}
                                </td>
                                <td
                                    className={`w-[16%] text-base ${
                                        isMedicalConditionTable &&
                                        item &&
                                        item.symptomControlled === "not controlled"
                                            ? "text-pink"
                                            : ""
                                    } whitespace-nowrap`}
                                >
                                    {
                                        isMedicalConditionTable
                                            ? SymptomControlOptions.find(
                                                (val) =>
                                                    val.value ===
                                                    (item?.symptomControlled ?? "").toString()
                                            )?.label || "Not Specified"
                                            : item.medicationName
                                    }
                                </td>
                                <td className="w-[12%] text-base text-gray-800 whitespace-nowrap capitalize">
                                    {item.source}
                                </td>
                                <td className="w-[11%] text-base text-gray-800 whitespace-nowrap">
                                    {formatDiagnosisDateForDisplay(item.date, item.diagnosisDateOption || item.surgeryDateOption || item.medicalStudyDateOption)}
                                </td>
                                <td className="w-[10%]">
                                    {item.comments && item.comments.length > 0 && (
                                        <div className="flex w-full justify-center items-center pt-2">
                                            <div
                                                ref={openTooltipId === `${isMedicalConditionTable ? "medCondComments" : "medStudyComments"}-${item.id}` ? tooltipWrapperRef : null}
                                                className="relative inline-flex cursor-pointer"
                                                data-tooltip-id={`${isMedicalConditionTable ? "medCondCommentsHover" : "medStudyCommentsHover"}-${item.id}`}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    const tooltipId = `${isMedicalConditionTable ? "medCondComments" : "medStudyComments"}-${item.id}`;
                                                    setOpenTooltipId(openTooltipId === tooltipId ? null : tooltipId);
                                                }}
                                            >
                                                <ReactSVG
                                                    src="/icons/paper-clip-new.svg"
                                                    className="w-6"
                                                />
                                                {openTooltipId !== `${isMedicalConditionTable ? "medCondComments" : "medStudyComments"}-${item.id}` && (
                                                    <NewToolTip
                                                        id={`${isMedicalConditionTable ? "medCondCommentsHover" : "medStudyCommentsHover"}-${item.id}`}
                                                        render={() => (
                                                            <div className="bg-white p-2 text-teal-300 text-body-sm">
                                                                Click to view comments
                                                            </div>
                                                        )}
                                                    />
                                                )}
                                                <NewToolTip
                                                    id={`${isMedicalConditionTable ? "medCondComments" : "medStudyComments"}-${item.id}`}
                                                    isOpen={openTooltipId === `${isMedicalConditionTable ? "medCondComments" : "medStudyComments"}-${item.id}`}
                                                    render={() => renderCommentsTooltip(item.comments)}
                                                    clickable={true}
                                                    handleHide={() => setOpenTooltipId(null)}
                                                    anchorSelect={`[data-tooltip-id="${isMedicalConditionTable ? "medCondCommentsHover" : "medStudyCommentsHover"}-${item.id}"]`}
                                                />
                                            </div>
                                        </div>
                                    )}
                                </td>
                                <td className="w-[13%] text-base text-gray-800 whitespace-nowrap">
                                    {item.status !== "inactive" && (
                                        <div className="flex items-start justify-start pl-24">
                                            {showConfirmation && (
                                                <input
                                                    type="checkbox"
                                                    className="!border-gray-200 mr-16"
                                                    checked={isCurrentEntryConfirmed(item.id)}
                                                    id={item.id}
                                                    onChange={() => onConfirm(item.id)}
                                                />
                                            )}
                                            <div className="flex flex-1 justify-start">
                                                <Menu
                                                    as="div"
                                                    className="relative inline-block text-left"
                                                >
                                                    {({ open }) => {
                                                        if (open && openMenuIndex !== i) {
                                                            setOpenMenuIndex(i);
                                                        } else if (!open && openMenuIndex === i) {
                                                            setOpenMenuIndex(null);
                                                        }

                                                        const shouldHideIcon = openMenuIndex === i - 1;

                                                        return (
                                                            <>
                                                                <div>
                                                                    <Menu.Button>
                                                                        <ReactSVG
                                                                            src={`/icons/triple-dot.svg`}
                                                                            className={`flex text-black !items-center w-6 ${
                                                                                shouldHideIcon
                                                                                    ? "opacity-0 pointer-events-none"
                                                                                    : ""
                                                                            }`}
                                                                        />
                                                                    </Menu.Button>
                                                                </div>
                                                                <Transition
                                                                    as={Fragment}
                                                                    enter="transition ease-out duration-100"
                                                                    enterFrom="transform translate-y-2 opacity-0 scale-95"
                                                                    enterTo="transform translate-y-0 opacity-100 scale-100"
                                                                    leave="transition ease-in duration-75"
                                                                    leaveFrom="transform translate-y-0 opacity-100 scale-100"
                                                                    leaveTo="transform translate-y-2 opacity-0 scale-95"
                                                                >
                                                                    <Menu.Items
                                                                        className={
                                                                            i === records.length - 1 || records.length === 1
                                                                            ? "absolute right-full mr-[6px] min-w-[70px] w-auto origin-top-right bg-white divide-y divide-gray-20 rounded-md shadow-lg ring-1 ring-black ring-opacity-5 top-0"
                                                                            : "absolute right-4 top-6 w-18 origin-top-right bg-white divide-y divide-gray-20 rounded-md shadow-lg ring-1 ring-black ring-opacity-5"
                                                                        }
                                                                    >
                                                                        <Menu.Item>
                                                                            <button
                                                                                className="block w-full text-left h-[30px] px-2.5 py-2.5 flex items-center border-b border-b-gray-20 hover:bg-gray-100"
                                                                                onClick={() => onEdit(item)}
                                                                            >
                                                                                Edit
                                                                            </button>
                                                                        </Menu.Item>
                                                                        <Menu.Item>
                                                                            <button
                                                                                className="block w-full text-left h-[30px] px-2.5 py-2.5 flex items-center border-b border-b-gray-20 hover:bg-gray-100"
                                                                                onClick={() => onDelete(item)}
                                                                            >
                                                                                Delete
                                                                            </button>
                                                                        </Menu.Item>
                                                                    </Menu.Items>
                                                                </Transition>
                                                            </>
                                                        );
                                                    }}
                                                </Menu>
                                            </div>
                                        </div>
                                    )}
                                    {item.status === "inactive" && (
                                        <div className="flex justify-center pl-16 mr-8">
                                            <ReactSVG
                                                src="/icons/undo.svg"
                                                className="w-6"
                                                onClick={() => {
                                                    onRestore(item);
                                                }}
                                            />
                                        </div>
                                    )}
                                </td>
                            </tr>
                        );
                        })}
                    </tbody>
                </table>
                </div>
            </div>
        </div>
    );
}

export default HistoryTable;